import React from 'react';

import * as Sentry from '@sentry/browser';

import { Alert, Button, Stack } from '@mui/material';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallbackTitle: string;
};

type ErrorBoundaryState = {
  hasError: boolean;
  retryCount: number;
};

const MAX_RETRY_COUNT = 3;

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    retryCount: 0,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        fallbackTitle: this.props.fallbackTitle,
      },
    });
  }

  handleRetry = () => {
    this.setState((state) => ({
      hasError: false,
      retryCount: state.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Alert severity="error">{this.props.fallbackTitle} failed to render.</Alert>
          <Button variant="text" onClick={this.handleRetry} disabled={this.state.retryCount >= MAX_RETRY_COUNT}>
            {this.state.retryCount >= MAX_RETRY_COUNT ? 'Retry limit reached' : 'Retry'}
          </Button>
        </Stack>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
